// Smart search panel with predictive product results, plus on-the-fly
// suggestions for color & size browse pages.
//
// Flow: user types → 200ms debounce → call Storefront predictiveSearch.
// In parallel we run a pure client-side match against COLOR_CATEGORIES,
// SIZE_BUCKETS and navGroups so people get a one-click jump into the
// right browse page even before products load.
//
// Recent searches are stored in localStorage (last 5).

import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, ArrowRight, Clock } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { fetchPredictive, formatMoney, type PredictiveProduct } from "@/lib/shopify";
import { COLOR_CATEGORIES } from "@/lib/colors";
import { SIZE_BUCKETS } from "@/lib/sizes";
import { navGroups } from "@/lib/navigation";
import { useT, localeHref, useLocale } from "@/lib/i18n";
import { hasConsent } from "@/lib/consent";

const RECENT_KEY = "fl.search.recent";
const POPULAR = ["mushroom", "peacock", "lyserød", "lysekrone", "vase", "kande"];

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
};

type Suggestion =
  | { kind: "color"; slug: string; label: string; italian: string; swatch: string }
  | { kind: "size"; slug: string; label: string; italian: string }
  | { kind: "category"; handle: string; label: string };

export function SearchPanel({ open, onOpenChange }: Props) {
  const { t, locale } = useT();
  const loc = useLocale();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const [q, setQ] = useState("");
  const [products, setProducts] = useState<PredictiveProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState<string[]>([]);

  // Focus the input + load recents when the panel opens; reset on close.
  useEffect(() => {
    if (open) {
      const raw = typeof window !== "undefined" ? localStorage.getItem(RECENT_KEY) : null;
      try {
        setRecent(raw ? (JSON.parse(raw) as string[]).slice(0, 5) : []);
      } catch {
        setRecent([]);
      }
      // small delay so the focus ring doesn't flash before the panel slides in
      setTimeout(() => inputRef.current?.focus(), 30);
      document.body.style.overflow = "hidden";
    } else {
      setQ("");
      setProducts([]);
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Debounced predictive search
  useEffect(() => {
    if (!open) return;
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setProducts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const id = setTimeout(async () => {
      try {
        const res = await fetchPredictive(trimmed, 6);
        setProducts(res.products);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(id);
  }, [q, open]);

  // Pure client-side match for color, size and category suggestions.
  const suggestions = useMemo<Suggestion[]>(() => {
    const trimmed = q.trim().toLowerCase();
    if (trimmed.length < 2) return [];
    const out: Suggestion[] = [];

    for (const c of COLOR_CATEGORIES) {
      const hay = [c.labelDa, c.labelEn, c.italian, c.slug, c.query]
        .join(" ")
        .toLowerCase();
      if (hay.includes(trimmed)) {
        out.push({
          kind: "color",
          slug: c.slug,
          label: locale === "da" ? c.labelDa : c.labelEn,
          italian: c.italian,
          swatch: c.swatch,
        });
      }
    }

    // Size: match either the bucket label or a number-with-cm in the query.
    const numMatch = trimmed.match(/(\d{1,3})\s*(?:cm)?/);
    const num = numMatch ? parseInt(numMatch[1], 10) : null;
    for (const b of SIZE_BUCKETS) {
      const hay = [b.labelDa, b.labelEn, b.italian, b.slug].join(" ").toLowerCase();
      const labelHit = hay.includes(trimmed);
      const numHit = num != null && num >= b.min && num < b.max;
      if (labelHit || numHit) {
        out.push({
          kind: "size",
          slug: b.slug,
          label: locale === "da" ? b.labelDa : b.labelEn,
          italian: b.italian,
        });
      }
    }

    // Categories from navGroups.
    for (const g of navGroups) {
      for (const c of g.collections) {
        const hay = [c.labelDa, c.labelEn, c.handle].join(" ").toLowerCase();
        if (hay.includes(trimmed)) {
          out.push({
            kind: "category",
            handle: c.handle,
            label: locale === "da" ? c.labelDa : c.labelEn,
          });
        }
      }
    }
    return out.slice(0, 6);
  }, [q, locale]);

  const persistRecent = (term: string) => {
    const next = [term, ...recent.filter((r) => r !== term)].slice(0, 5);
    setRecent(next);
    if (!hasConsent("preferences")) return;
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {
      // ignore quota / private mode
    }
  };

  const submit = (term?: string) => {
    const final = (term ?? q).trim();
    if (!final) return;
    persistRecent(final);
    onOpenChange(false);
    navigate({ to: localeHref(loc, `/sog?q=${encodeURIComponent(final)}`) });
  };

  const goTo = (path: string) => {
    onOpenChange(false);
    navigate({ to: localeHref(loc, path) });
  };

  if (!open) return null;

  const trimmed = q.trim();
  const hasQuery = trimmed.length >= 2;
  const empty = hasQuery && !loading && products.length === 0 && suggestions.length === 0;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-stretch justify-center bg-foreground/40 backdrop-blur-sm md:items-start"
      onClick={() => onOpenChange(false)}
      role="dialog"
      aria-modal="true"
      aria-label={t("header.search")}
    >
      <div
        className="flex h-full w-full flex-col overflow-hidden bg-background shadow-2xl md:mt-28 md:h-auto md:max-w-3xl md:rounded-sm md:border md:border-border"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input row */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="flex items-center gap-3 border-b border-border px-5 py-4"
        >
          <Search className="h-5 w-5 text-muted-foreground" />
          <input
            ref={inputRef}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("header.searchPlaceholder")}
            className="flex-1 bg-transparent text-base text-foreground outline-none placeholder:text-muted-foreground"
            autoComplete="off"
            spellCheck={false}
          />
          {q && (
            <button
              type="button"
              onClick={() => {
                setQ("");
                inputRef.current?.focus();
              }}
              className="text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground"
              aria-label={t("search.clear")}
            >
              {t("search.clear")}
            </button>
          )}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="ml-1 inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
            aria-label={t("search.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </form>

        {/* Results body */}
        <div className="flex-1 overflow-y-auto md:max-h-[70vh] md:flex-none">
          {!hasQuery && (
            <div className="px-5 py-6">
              {recent.length > 0 && (
                <section className="mb-6">
                  <p className="mb-3 text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
                    {t("search.recent")}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {recent.map((r) => (
                      <button
                        key={r}
                        onClick={() => {
                          setQ(r);
                          submit(r);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-foreground hover:border-foreground/40"
                      >
                        <Clock className="h-3 w-3 text-muted-foreground" />
                        {r}
                      </button>
                    ))}
                  </div>
                </section>
              )}

              <section>
                <p className="mb-3 text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
                  {t("search.popular")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {POPULAR.map((p) => (
                    <button
                      key={p}
                      onClick={() => {
                        setQ(p);
                        submit(p);
                      }}
                      className="rounded-full border border-border px-3 py-1.5 text-xs text-foreground hover:border-foreground/40"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </section>

              {/* Always-on shortcuts */}
              <section className="mt-8 grid grid-cols-2 gap-3">
                {COLOR_CATEGORIES.slice(0, 4).map((c) => (
                  <button
                    key={c.slug}
                    onClick={() => goTo(`/farver/${c.slug}`)}
                    className="flex items-center gap-3 rounded-sm border border-border px-3 py-2 text-left hover:border-foreground/40"
                  >
                    <span
                      className="block h-4 w-4 rounded-full border border-foreground/15"
                      style={{ background: c.swatch }}
                      aria-hidden
                    />
                    <span className="text-sm text-foreground">
                      {locale === "da" ? c.labelDa : c.labelEn}
                    </span>
                  </button>
                ))}
              </section>
            </div>
          )}

          {hasQuery && (
            <div className="divide-y divide-border">
              {/* Products */}
              {(loading || products.length > 0) && (
                <section className="px-5 py-4">
                  <p className="mb-3 text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
                    {t("search.products")}
                  </p>
                  {loading && products.length === 0 ? (
                    <div className="space-y-3">
                      {[0, 1, 2].map((i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="h-12 w-12 animate-pulse rounded-sm bg-muted" />
                          <div className="flex-1 space-y-1.5">
                            <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
                            <div className="h-2.5 w-1/4 animate-pulse rounded bg-muted" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <ul className="space-y-1">
                      {products.map((p) => (
                        <li key={p.id}>
                          <button
                            onClick={() => {
                              persistRecent(trimmed);
                              goTo(`/products/${p.handle}`);
                            }}
                            className="flex w-full items-center gap-3 rounded-sm p-2 text-left transition-colors hover:bg-foreground/5"
                          >
                            <span className="block h-12 w-12 shrink-0 overflow-hidden rounded-sm bg-muted">
                              {p.featuredImage?.url && (
                                <img
                                  src={p.featuredImage.url}
                                  alt={p.featuredImage.altText ?? p.title}
                                  className="h-full w-full object-cover"
                                  loading="lazy"
                                />
                              )}
                            </span>
                            <span className="flex-1">
                              <span className="block text-sm text-foreground">
                                {p.title}
                              </span>
                              <span className="block text-xs text-muted-foreground">
                                {formatMoney(p.priceRange.minVariantPrice)}
                                {!p.availableForSale && (
                                  <span className="ml-2 text-foreground/50">
                                    · udsolgt
                                  </span>
                                )}
                              </span>
                            </span>
                            <ArrowRight className="h-4 w-4 text-muted-foreground" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {!loading && products.length > 0 && (
                    <button
                      onClick={() => submit()}
                      className="mt-3 inline-flex items-center gap-1 text-xs font-medium uppercase tracking-[0.22em] text-terracotta hover:text-foreground"
                    >
                      {t("search.viewAll", { n: products.length })} →
                    </button>
                  )}
                </section>
              )}

              {/* Color/size/category suggestions */}
              {suggestions.length > 0 && (
                <section className="px-5 py-4">
                  <p className="mb-3 text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
                    {t("search.categories")}
                  </p>
                  <ul className="space-y-1">
                    {suggestions.map((s) => {
                      if (s.kind === "color") {
                        return (
                          <li key={`color-${s.slug}`}>
                            <button
                              onClick={() => goTo(`/farver/${s.slug}`)}
                              className="flex w-full items-center gap-3 rounded-sm p-2 text-left text-sm hover:bg-foreground/5"
                            >
                              <span
                                className="block h-4 w-4 rounded-full border border-foreground/15"
                                style={{ background: s.swatch }}
                                aria-hidden
                              />
                              <span className="flex-1 text-foreground">{s.label}</span>
                              <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                                {t("search.colors")} · {s.italian}
                              </span>
                            </button>
                          </li>
                        );
                      }
                      if (s.kind === "size") {
                        return (
                          <li key={`size-${s.slug}`}>
                            <button
                              onClick={() => goTo(`/storrelse/${s.slug}`)}
                              className="flex w-full items-center gap-3 rounded-sm p-2 text-left text-sm hover:bg-foreground/5"
                            >
                              <span className="block h-4 w-4 rounded-full border border-foreground/30" />
                              <span className="flex-1 text-foreground">{s.label}</span>
                              <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                                {t("search.sizes")} · {s.italian}
                              </span>
                            </button>
                          </li>
                        );
                      }
                      return (
                        <li key={`cat-${s.handle}`}>
                          <button
                            onClick={() => goTo(`/collections/${s.handle}`)}
                            className="flex w-full items-center gap-3 rounded-sm p-2 text-left text-sm hover:bg-foreground/5"
                          >
                            <ArrowRight className="h-4 w-4 text-muted-foreground" />
                            <span className="flex-1 text-foreground">{s.label}</span>
                            <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                              {t("search.categories")}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}

              {empty && (
                <section className="px-5 py-10 text-center">
                  <p className="text-sm text-foreground">
                    {t("search.empty", { q: trimmed })}
                  </p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {t("search.emptyHint")}
                  </p>
                </section>
              )}
            </div>
          )}
        </div>

        <div className="hidden border-t border-border px-5 py-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground md:flex md:items-center md:justify-between">
          <span>↵ for at se alle resultater</span>
          <span>esc for at lukke</span>
        </div>
      </div>
    </div>
  );
}
